import { useState, useCallback } from 'react';
import { usePlatform } from '../shared/usePlatform';
import { getPlatformConfig } from '../../utils/shared/platformUtils';

/**
 * Mobile-specific pull-to-refresh hook
 * Provides pull-to-refresh functionality with haptic feedback
 */
export const usePullToRefresh = (onRefresh) => {
  const { isMobile } = usePlatform();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [isPulling, setIsPulling] = useState(false);
  
  const config = getPlatformConfig('mobile');
  const threshold = config.scrollThreshold;

  const handleRefresh = useCallback(async () => {
    if (!isMobile || isRefreshing) return;

    setIsRefreshing(true);
    
    try {
      // Trigger haptic feedback for refresh start
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      
      await onRefresh();
      
      // Trigger haptic feedback for refresh success
      if (navigator.vibrate) {
        navigator.vibrate([50, 50, 50]);
      }
    } catch (error) {
      console.error('Pull-to-refresh failed:', error);
      
      // Trigger haptic feedback for refresh error
      if (navigator.vibrate) {
        navigator.vibrate([200, 100, 200]);
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [isMobile, isRefreshing, onRefresh]);

  const handlePullStart = useCallback(() => {
    if (!isMobile) return;
    
    // Light haptic feedback for pull start
    if (navigator.vibrate) {
      navigator.vibrate(20);
    }
  }, [isMobile]);

  const handlePullThreshold = useCallback(() => {
    if (!isMobile || isPulling) return;
    
    setIsPulling(true);
    
    // Medium haptic feedback for reaching threshold
    if (navigator.vibrate) {
      navigator.vibrate(100);
    }
  }, [isMobile, isPulling]);

  const handlePullReset = useCallback(() => {
    if (!isMobile) return;
    
    setIsPulling(false);
    setPullDistance(0);
  }, [isMobile]);

  const updatePullDistance = useCallback((distance) => {
    if (!isMobile) return;
    
    setPullDistance(distance);
    
    // Check if we've reached the threshold
    if (distance >= threshold && !isPulling) {
      handlePullThreshold();
    } else if (distance < threshold && isPulling) {
      handlePullReset();
    }
  }, [isMobile, threshold, isPulling, handlePullThreshold, handlePullReset]);

  return {
    // State
    isRefreshing,
    isPulling,
    pullDistance,
    
    // Actions
    handleRefresh,
    handlePullStart,
    updatePullDistance,
    handlePullReset,
    
    // Configuration
    threshold,
    config,
    
    // Platform check
    isMobile,
  };
};
